<template>
  <div class="withdraw-modal-overlay">
    <transition name="modal-expand">
    <div v-if="isVisible" class="withdraw-modal" @click.stop>
      <div class="modal-content">
        <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
        <h2>Withdraw Logbook ID: {{ logbook.logbook_id }}</h2>

        <p class="withdraw-info">
          This Logbook contains entries and cannot be deleted normally. Withdrawing it removes it from
          the {{ $labels.project }} and unassigns its users. The entries are kept for the audit trail
          and the reason below is recorded.
        </p>
        <p class="withdraw-summary">
          <strong>{{ logbook.name }}</strong><br />
          Trainee: {{ logbook.trainee_full_name || 'Unknown' }}
        </p>

        <form @submit.prevent="withdrawLogbook">
          <div class="form-group">
            <label for="withdraw_reason">Reason</label>
            <select id="withdraw_reason" class="form-control rounded" v-model="reason">
              <option value="" disabled>Select a reason</option>
              <option value="medical">Medical</option>
              <option value="personal">Personal</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-group">
            <label for="withdraw_note">Details</label>
            <textarea id="withdraw_note" class="form-control rounded" rows="4" maxlength="2000"
              placeholder="e.g. Trainee left the course due to a medical issue and will return on a future session."
              v-model="note"></textarea>
          </div>
          <div class="form-group">
            <label for="withdraw_confirm">Type the Logbook ID ({{ logbook.logbook_id }}) to confirm</label>
            <input id="withdraw_confirm" type="text" class="form-control rounded" v-model="confirmId" autocomplete="off" />
          </div>

          <div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>

          <button class="btn rounded withdraw-button" type="submit" :disabled="!canSubmit || submitting">
            {{ submitting ? 'Withdrawing...' : 'Withdraw Logbook' }}
          </button>
        </form>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WithdrawLogbookModal',

  props: {
    logbook: { type: Object, required: true },
  },

  data() {
    return {
      reason: '',
      note: '',
      confirmId: '',
      errorMessage: '',
      submitting: false,
      isVisible: false,
    };
  },

  computed: {
    canSubmit() {
      return this.reason
        && this.note.trim().length >= 3
        && this.confirmId.trim() === String(this.logbook.logbook_id);
    },
  },

  methods: {
    async withdrawLogbook() {
      if (!this.canSubmit) return;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      this.submitting = true;
      this.errorMessage = '';

      try {
        await axios.post(`/api/logbooks/${this.logbook.logbook_id}/withdraw`, {
          reason: this.reason,
          note: this.note.trim(),
        }, { headers });

        window.alert('Logbook withdrawn successfully');
        this.$emit('withdrawn', this.logbook.logbook_id);
      } catch (error) {
        if (error.isOffline === true && error.queued) {
          window.alert('You are offline. The withdrawal has been queued and will sync later.');
          this.$emit('withdrawn', this.logbook.logbook_id);
          return;
        }

        if (error.response?.status === 422) {
          this.errorMessage = 'Please select a reason and enter details.';
        } else if (error.response?.status === 403) {
          this.errorMessage = 'You do not have permission to withdraw Logbooks.';
        } else {
          this.errorMessage = 'An error occurred while withdrawing the Logbook.';
        }
      } finally {
        this.submitting = false;
      }
    },
    close() {
      this.$emit('close');
    },
  },

  mounted() {
    this.isVisible = true;
  },
}
</script>

<style scoped>
.withdraw-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.withdraw-modal {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 80%;
  max-width: 650px;
  padding: 20px;
  position: relative;
  max-height: 95%;
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 15px;
  border: 1px solid #dddddd;
  border-radius: 3px;
}

h2 {
  margin-top: 10px;
  font-size: 26px;
  margin-bottom: 20px;
}

.withdraw-info {
  font-size: 14px;
  color: #555;
}

.withdraw-summary {
  font-size: 14px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-control {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 5px;
  padding: 6px;
}

button.withdraw-button {
  width: 200px;
  height: 40px;
  margin-top: 10px;
  border: 1px solid #a82222;
  background: #dc3545;
  color: white;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

button.withdraw-button:hover:not(:disabled) {
  background-color: #a82222;
}

button.withdraw-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.alert {
  position: relative;
  padding: .75rem 1.25rem;
  margin-bottom: 0;
  border: 1px solid transparent;
  border-radius: .25rem;
}
</style>
